/**
 * Tipi ed enum condivisi tra backend e frontend.
 * Rispecchiano gli enum Prisma: se cambia lo schema, cambia anche qui.
 */
export * from "./canonical-symbols.js";
export * from "./symbol-spec.js";
/**
 * Toglie da un testo ogni occorrenza dei segreti indicati.
 *
 * Sta in `shared` e non nel backend perche' serve in DUE posti che non possono
 * importarsi a vicenda: il **driver** (dove il messaggio del gateway nasce) e il
 * **backend** (dove i messaggi nostri finiscono in database e nelle risposte).
 * Due implementazioni della stessa regola sono la forma di difetto che questo
 * progetto ha gia' pagato: la regola sta in un posto, e i chiamanti la chiamano.
 *
 * La soglia dei tre caratteri non e' prudenza: sotto, un segreto cortissimo
 * cancellerebbe pezzi di testo a caso e renderebbe illeggibile il messaggio
 * senza nascondere niente che valga.
 */
export function redactSecrets(text, secrets) {
    return secrets.reduce((t, s) => (s && s.length >= 3 ? t.split(s).join("***") : t), text);
}
export const USER_ROLES = ["ADMIN", "OPERATORE", "CONTABILE", "VIEWER", "CLIENT"];
export const ACCOUNT_ROLES = ["PROP", "BROKER", "NONE"];
export const CYCLE_PHASES = ["PHASE_1", "PHASE_2", "FUNDED"];
export const CYCLE_STATES = ["ACTIVE", "CLOSED_PROFIT", "CLOSED_LOSS", "ABANDONED"];
export const SESSION_STATES = ["ACTIVE", "PAUSED", "AWAITING_PAYOUT", "AWAITING_BROKER", "COMPLETED", "FAILED"];
/**
 * PERCHE' UNA FASE E' FERMA. Obbligatorio per fermarla.
 *
 * Sta qui e non in due copie perche' la lista la usano in tre: il backend per
 * accettarla, il browser per proporla, tutt'e due per scriverla in chiaro. Due
 * liste divergono, e il giorno in cui divergessero il browser proporrebbe un
 * motivo che il backend rifiuta.
 */
export const HOLD_REASONS = ["KYC", "PAYOUT", "CLIENTE", "ALTRO"];
export const PAIR_STATES = ["PENDING", "BOTH_OPEN", "ONE_LEG", "CLOSED", "FAILED_OPEN", "ABANDONED"];
export const CLOSE_REASONS = [
    "PROP_SL_HIT",
    "PROP_TP_HIT",
    "POSITION_DD",
    "DAILY_DD_GUARD",
    "PROP_CLOSED_FIRST",
    "BROKER_CLOSED_FIRST",
    "MANUAL_CLOSE",
    "PROP_BREACH",
    "SESSION_COMPLETE",
    "DESYNC",
    "CLOSED_BY_SERVER",
];
export const DD_BASES = [
    "STATIC_INITIAL",
    "STATIC_BALANCE",
    "EOD_BALANCE",
    "TRAILING_HIGH_WATER",
    "TRAILING_LOCKED_AT_INITIAL",
];
export const RISK_EVENT_KINDS = [
    "NAKED_LEG_TIMEOUT",
    "DD_THRESHOLD",
    "DAILY_DD_THRESHOLD",
    "SESSION_FAILED",
    "ACCOUNT_DISCONNECTED",
    "PROXY_DOWN",
    "ORDER_REASON_UNEXPECTED",
    "EXECUTION_DESYNC",
    "LEVELS_DESYNC",
    "SYMBOL_SPEC_CHANGED",
    "COMMISSION_CHANGED",
    "MAX_SESSIONS_REACHED",
    "MIRROR_UNREACHABLE",
    "LEG_VANISHED",
    "PAIR_CLOSED_BY_SERVER",
    "PAIR_ABANDONED",
    "BROKER_CASH_MOVE",
];
// ---------------------------------------------------------------------------
// Etichette in italiano per la UI. Tenute qui per non sparpagliare stringhe.
// ---------------------------------------------------------------------------
export const LABELS = {
    accountRole: {
        PROP: "Prop",
        BROKER: "Broker",
        NONE: "Nessun ruolo",
    },
    cyclePhase: {
        PHASE_1: "Fase 1",
        PHASE_2: "Fase 2",
        FUNDED: "Funded",
    },
    cycleState: {
        ACTIVE: "Attivo",
        // Il ciclo chiude alla bruciatura del prop: e' l'esito che si cerca.
        CLOSED_PROFIT: "Chiuso in profitto",
        CLOSED_LOSS: "Chiuso in perdita",
        ABANDONED: "Abbandonato",
    },
    sessionState: {
        ACTIVE: "Attiva",
        PAUSED: "In pausa",
        // Il periodo e' chiuso e il tratto nuovo non e' ancora nato: si aspetta
        // il bonifico, e finche' non arriva non si apre niente.
        AWAITING_PAYOUT: "In attesa di payout",
        // Il conto prop c'e', il broker no: nessuna apertura finche' non arriva
        // (`POST /sessions/:id/attach-broker`).
        AWAITING_BROKER: "In attesa del conto broker",
        COMPLETED: "Fase superata",
        // Nel modello Match la bruciatura e' l'esito profittevole: l'etichetta
        // non deve suggerire un errore.
        FAILED: "Conto bruciato",
    },
    holdReason: {
        KYC: "KYC",
        /*
         * «Attesa bonifico» e non «Payout in attesa»: quello somiglia troppo a
         * `AWAITING_PAYOUT` = «In attesa di payout», che e' lo stato CONTABILE —
         * il tratto chiuso, deciso dalla chiusura del periodo — mentre questo e'
         * una decisione umana di non operare. Il valore resta `PAYOUT`: a cambiare
         * e' come si legge, non che cosa si e' scelto.
         */
        PAYOUT: "Attesa bonifico",
        CLIENTE: "Attesa del cliente",
        // Chi lo sceglie scrive che cosa aspetta: la nota li' e' obbligatoria.
        ALTRO: "Altro",
    },
    pairState: {
        PENDING: "In apertura",
        BOTH_OPEN: "Entrambe aperte",
        ONE_LEG: "Gamba scoperta",
        CLOSED: "Chiusa",
        FAILED_OPEN: "Apertura fallita",
        // E' andata a mercato, e non sappiamo piu' dove sia finita: non e' una
        // chiusura, ed e' un'azione umana.
        ABANDONED: "Coppia abbandonata",
    },
    closeReason: {
        PROP_SL_HIT: "Stop loss sul prop",
        PROP_TP_HIT: "Take profit sul prop",
        POSITION_DD: "Budget DD della posizione esaurito",
        DAILY_DD_GUARD: "Protezione DD giornaliero",
        PROP_CLOSED_FIRST: "Prop chiuso per primo",
        BROKER_CLOSED_FIRST: "Broker chiuso per primo",
        MANUAL_CLOSE: "Chiusura manuale",
        PROP_BREACH: "Conto chiuso dalla prop firm",
        SESSION_COMPLETE: "Fase completata",
        DESYNC: "Chiusura forzata per perdita di sincronia",
        // Chiusa dal server, e il prezzo non dice se e' stato lo SL o il TP.
        CLOSED_BY_SERVER: "Chiusa dal server, motivo non deducibile",
    },
    /** Grandezze del ciclo e della sessione, come si chiamano in interfaccia. */
    terms: {
        recoverAmount: "Da recuperare",
        costsRecovered: "Costi recuperati",
        feesPaid: "Fee pagate",
        realRealizedPnl: "P&L realizzato sul reale",
        adjustments: "Rettifiche",
    },
    /**
     * I tipi di evento, detti come li direbbe una persona.
     *
     * Il messaggio dell'evento racconta il caso singolo; questa e' l'etichetta
     * della CLASSE, quella che va in un filtro e in un'intestazione di colonna.
     * `SESSION_FAILED` si chiama «Conto bruciato» come ovunque: e' l'esito
     * voluto, e l'etichetta non deve suggerire un guasto.
     */
    riskEventKind: {
        NAKED_LEG_TIMEOUT: "Gamba scoperta",
        DD_THRESHOLD: "Drawdown oltre soglia",
        DAILY_DD_THRESHOLD: "Drawdown giornaliero oltre soglia",
        SESSION_FAILED: "Conto bruciato",
        ACCOUNT_DISCONNECTED: "Conto disconnesso",
        PROXY_DOWN: "Proxy irraggiungibile",
        ORDER_REASON_UNEXPECTED: "Origine ordine inattesa",
        EXECUTION_DESYNC: "Esecuzione non allineata",
        LEVELS_DESYNC: "Livelli non allineati",
        SYMBOL_SPEC_CHANGED: "Specifiche del simbolo cambiate",
        COMMISSION_CHANGED: "Commissione cambiata",
        MAX_SESSIONS_REACHED: "Sessioni del gateway esaurite",
        MIRROR_UNREACHABLE: "Gateway irraggiungibile",
        LEG_VANISHED: "Gamba sparita da una coppia aperta",
        PAIR_CLOSED_BY_SERVER: "Coppia chiusa dal server",
        PAIR_ABANDONED: "Coppia abbandonata",
        BROKER_CASH_MOVE: "Movimento di cassa sul broker",
    },
    riskSeverity: {
        INFO: "Informativo",
        WARNING: "Avviso",
        CRITICAL: "Critico",
    },
    ddBasis: {
        STATIC_INITIAL: "Statico su saldo iniziale",
        STATIC_BALANCE: "Statico su saldo corrente",
        EOD_BALANCE: "Su saldo di fine giornata",
        TRAILING_HIGH_WATER: "Trailing sul massimo",
        TRAILING_LOCKED_AT_INITIAL: "Trailing con blocco a saldo iniziale",
    },
};
/**
 * IL MOTIVO DELLA SOSTA IN CHIARO, per un rifiuto o per una riga.
 *
 * Tre casi, e nessuno inventa niente:
 *   - nessun motivo → «motivo non registrato». Sono le Fasi fermate PRIMA che
 *     il motivo fosse obbligatorio: «non lo so» non e' «nessun motivo»;
 *   - `ALTRO` → la nota, che li' e' obbligatoria: scrivere «altro» e basta
 *     sarebbe la stessa assenza con un'altra faccia;
 *   - gli altri → l'etichetta, piu' la nota se c'e'.
 *
 * Una funzione sola per il backend e per il browser: il motivo che si legge
 * nel rifiuto dell'apertura e quello che si legge nella riga sono lo stesso
 * testo, e non c'e' un secondo posto dove possa dire un'altra cosa.
 */
export function holdText(hold) {
    if (!hold?.reason)
        return "motivo non registrato";
    if (hold.reason === "ALTRO")
        return hold.note?.trim() || LABELS.holdReason.ALTRO;
    const nota = hold.note?.trim();
    return nota ? `${LABELS.holdReason[hold.reason]} — ${nota}` : LABELS.holdReason[hold.reason];
}
//# sourceMappingURL=index.js.map